import { ref, onMounted, computed } from "vue";
import { getArticlesList, parseMarkdown } from "@/utils/markdownLoader";
import { getTopicById } from "@/config/topics";

export function useBlogPosts() {
  const posts = ref([]);
  const loading = ref(false);

  // 加载 mikus-post-bg 文件夹中的所有图片
  const mikuImages = [
    require("@/assets/pictures/mikus-post-bg/miku1.jpg"),
    require("@/assets/pictures/mikus-post-bg/miku2.jpg"),
    require("@/assets/pictures/mikus-post-bg/miku3.jpg"),
    require("@/assets/pictures/mikus-post-bg/miku4.jpg"),
    require("@/assets/pictures/mikus-post-bg/other.jpg"),
  ];

  // 随机选择一张 miku 图片
  const getRandomMikuImage = () => {
    const randomIndex = Math.floor(Math.random() * mikuImages.length);
    return mikuImages[randomIndex];
  };

  // 加载所有文章的元数据
  const loadPosts = () => {
    loading.value = true;
    try {
      const articlesList = getArticlesList();
      console.log("Loading articles:", articlesList);

      // 使用 require.context 批量导入
      const articlesContext = require.context(
        "@/assets/articles",
        false,
        /\.md$/
      );
      const postsData = [];

      articlesList.forEach((article) => {
        try {
          // 使用 context 加载文件
          const content = articlesContext(article.key);

          // raw-loader 返回的是字符串，但可能被包装在 default 中
          const markdownContent =
            typeof content === "string" ? content : content.default || content;

          const { metadata } = parseMarkdown(markdownContent);
          console.log(`Parsed metadata for ${article.filename}:`, metadata);

          // 只添加有效的文章（有标题的）
          if (metadata.title) {
            // 获取主题信息，如果没有匹配则归为"其他"
            const topicSlug = metadata.topic
              ? metadata.topic.toLowerCase()
              : "other";
            const topic = getTopicById(topicSlug) || getTopicById("other");

            postsData.push({
              id: article.id,
              title: metadata.title || "Untitled",
              subtitle: metadata.subtitle || "",
              excerpt: metadata.excerpt || "",
              topic: topic.name,
              topicId: topic.id,
              topicSlug: topic.slug,
              date: metadata.date || "",
              author: metadata.author || "",
              image: metadata.image
                ? require(`@/assets/pictures/${metadata.image}`)
                : getRandomMikuImage(),
              link: `/blog/${article.id}`,
            });
          }
        } catch (error) {
          console.error(`Error loading article ${article.filename}:`, error);
        }
      });

      // 按日期排序（最新的在前），日期相同时按标题倒序
      postsData.sort((a, b) => {
        // 首先按日期排序
        if (a.date && b.date) {
          const dateCompare = new Date(b.date) - new Date(a.date);
          if (dateCompare !== 0) {
            return dateCompare;
          }
        } else if (!a.date && b.date) {
          return 1; // 没有日期的排在后面
        } else if (a.date && !b.date) {
          return -1; // 有日期的排在前面
        }

        // 日期相同或都没有日期时，按标题倒序（Z-A）
        const titleA = a.title || "";
        const titleB = b.title || "";
        return titleB.localeCompare(titleA, "zh-CN");
      });

      posts.value = postsData;
      console.log("Final posts:", posts.value);
    } catch (error) {
      console.error("Error loading posts:", error);
    } finally {
      loading.value = false;
    }
  };

  // 根据主题筛选文章
  const getPostsByTopic = (topicId) => {
    return computed(() => {
      if (!topicId || topicId === "all") {
        return posts.value;
      }
      return posts.value.filter((post) => post.topicId === topicId);
    });
  };

  // 获取主题统计
  const getTopicStats = computed(() => {
    const stats = {};
    posts.value.forEach((post) => {
      const topicId = post.topicId || "other";
      stats[topicId] = (stats[topicId] || 0) + 1;
    });
    return stats;
  });

  // 使用 onMounted 确保在组件挂载后加载
  onMounted(() => {
    loadPosts();
  });

  return {
    posts,
    loading,
    loadPosts,
    getPostsByTopic,
    getTopicStats,
  };
}
